// File: src/components/ApplyModal.jsx
import React, { useEffect, useId, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import "../components/ApplyModal.css"; // keep your path

/**
 * Application modal that POSTs to /api/apply.
 * Backend base URL:
 *  - process.env.REACT_APP_API_BASE
 *  - window.__API_BASE__ (optional global)
 *  - default: http://localhost:5050
 */
export default function ApplyModal({
  open,
  jobTitle = "",
  roleOptions = [],
  allowRoleSelect = false,
  onClose,
  onSubmit, // optional callback after success
}) {
  const titleId = useId();
  const dialogRef = useRef(null);

  const API_BASE =
    (typeof process !== "undefined" && process.env?.REACT_APP_API_BASE) ||
    (typeof window !== "undefined" && window.__API_BASE__) ||
    "http://localhost:5050";

  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onEsc);
    requestAnimationFrame(() => {
      dialogRef.current?.querySelector("input,select,textarea,button")?.focus();
      dialogRef.current?.scrollTo?.({ top: 0 });
    });
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  if (!open) return null;

  const initialRole = allowRoleSelect
    ? jobTitle || roleOptions?.[0] || ""
    : jobTitle || "";

  // Match backend (zod) limits: name>=2, role(job)>=2, proposal>=10
  const schema = Yup.object({
    name: Yup.string().trim().min(2, "Name must be at least 2 characters.").required("Your name is required."),
    email: Yup.string().trim().email("Enter a valid email.").required("Email is required."),
    role: Yup.string().trim().min(2, "Role must be at least 2 characters.").required("Select a role."),
    proposal: Yup.string().trim().min(10, "Proposal must be at least 10 characters.").required("Add a brief proposal / cover letter."),
  });

  return (
    <div className="apply-overlay" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div className="apply-backdrop" role="presentation" onMouseDown={onClose} />
      <div className="apply-sheet" ref={dialogRef} onMouseDown={(e) => e.stopPropagation()}>
        <header className="apply-header">
          <div className="apply-title-wrap">
            <span className="apply-kicker">Application</span>
            <h2 id={titleId} className="apply-title">
              {allowRoleSelect ? "Apply at Ezclinicstaffing" : jobTitle || "Apply"}
            </h2>
          </div>
          <button className="apply-close" aria-label="Close" onClick={onClose}>×</button>
        </header>

        <Formik
          enableReinitialize
          initialValues={{ name: "", email: "", role: initialRole, proposal: "" }}
          validationSchema={schema}
          onSubmit={async (values, helpers) => {
            helpers.setStatus(undefined);
            try {
              const payload = {
                name: values.name.trim(),
                email: values.email.trim(),
                job: values.role.trim(),       // backend expects `job`
                proposal: values.proposal.trim(),
              };

              const res = await fetch(`${API_BASE}/api/apply`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
              });

              const data = await res.json().catch(() => ({}));

              if (!res.ok || !data?.ok) {
                // Map server-side validation to fields if provided
                const fe = data?.errors?.fieldErrors;
                if (fe) {
                  if (fe.name?.[0]) helpers.setFieldError("name", fe.name[0]);
                  if (fe.email?.[0]) helpers.setFieldError("email", fe.email[0]);
                  if (fe.job?.[0]) helpers.setFieldError("role", fe.job[0]); // map job -> role
                  if (fe.proposal?.[0]) helpers.setFieldError("proposal", fe.proposal[0]);
                }
                throw new Error(data?.message || "Submission failed. Please fix the errors and try again.");
              }

              onSubmit?.(payload);
              helpers.setSubmitting(false);
              onClose?.();
            } catch (err) {
              helpers.setSubmitting(false);
              helpers.setStatus({ error: err.message || "Network error. Please try again." });
            }
          }}
        >
          {({ isSubmitting, isValid, status, values }) => (
            <Form className="apply-form" noValidate>
              {status?.error && (
                <div
                  role="alert"
                  style={{
                    border: "1px solid #fecaca",
                    background: "#fef2f2",
                    color: "#991b1b",
                    borderRadius: 12,
                    padding: "0.75rem 1rem",
                    fontWeight: 600,
                  }}
                >
                  {status.error}
                </div>
              )}

              <div className="field">
                <label htmlFor="name">Full name</label>
                <Field id="name" name="name" type="text" placeholder="Jane Doe" autoComplete="name" />
                <ErrorMessage component="p" className="field-error" name="name" />
              </div>

              <div className="field">
                <label htmlFor="email">Email</label>
                <Field
                  id="email"
                  name="email"
                  type="email"
                  placeholder="jane@company.com"
                  inputMode="email"
                  autoComplete="email"
                />
                <ErrorMessage component="p" className="field-error" name="email" />
              </div>

              <div className="field">
                <label htmlFor="role">Role</label>
                {allowRoleSelect ? (
                  <div className="select">
                    <Field as="select" id="role" name="role" aria-describedby="role-help">
                      {(roleOptions?.length ? roleOptions : [initialRole || ""]).map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </Field>
                    <span className="select-chevron" aria-hidden>▾</span>
                  </div>
                ) : (
                  <input type="text" value={values.role} readOnly className="readonly" />
                )}
                <p id="role-help" className="field-help">Choose the role you’re applying for.</p>
                <ErrorMessage component="p" className="field-error" name="role" />
              </div>

              <div className="field">
                <label htmlFor="proposal">Proposal / Cover letter</label>
                <Field
                  as="textarea"
                  id="proposal"
                  name="proposal"
                  rows={6}
                  placeholder="Brief pitch, experience, and availability…"
                />
                <ErrorMessage component="p" className="field-error" name="proposal" />
              </div>

              <div className="apply-actions">
                <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn primary" disabled={isSubmitting || !isValid}>
                  {isSubmitting ? "Submitting…" : "Submit Application"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}
